import { AIM, aimCos } from "@eldrun/sim";
import { sweepSphere, type CameraWorld } from "./camera-rig.ts";

/** Afinação da mira (F55). */
export const CROSSHAIR = {
  /** Alcance do raio da cruz (m): além disto, a mira vai ao fim do raio. */
  range: 120,
  /** Trecho de cada varredura (m) e passo do terreno nela. */
  chunk: 8,
  step: 0.5,
  /** O cliente aproxima a mira da câmera acima disto (o servidor aceita até 25°). */
  maxDeviation: (24 * Math.PI) / 180,
  /** Ponto mais perto do olho que isto (m): a direção ficaria instável; vale a câmera. */
  minDistance: 0.6,
} as const;

const COS_MAX = Math.cos(CROSSHAIR.maxDeviation);

/**
 * Entrada de um raio (origem O, direção unitária D) num cilindro vertical: distância ao longo do raio
 * a partir de `t0`, ou `Infinity`.
 */
export function rayCylinder(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, t0: number, t1: number, cx: number, cz: number, radius: number, bottom: number, top: number): number {
  const fx = ox - cx;
  const fz = oz - cz;
  const qa = dx * dx + dz * dz;
  const qc = fx * fx + fz * fz - radius * radius;
  let lo = t0;
  let hi = t1;
  if (qa < 1e-12) {
    if (qc > 0) return Infinity;
  } else {
    const qb = 2 * (fx * dx + fz * dz);
    const disc = qb * qb - 4 * qa * qc;
    if (disc < 0) return Infinity;
    const root = Math.sqrt(disc);
    lo = Math.max(lo, (-qb - root) / (2 * qa));
    hi = Math.min(hi, (-qb + root) / (2 * qa));
    if (lo > hi) return Infinity;
  }
  if (Math.abs(dy) < 1e-12) return oy >= bottom && oy <= top ? lo : Infinity;
  const ya = (bottom - oy) / dy;
  const yb = (top - oy) / dy;
  const start = Math.max(lo, ya < yb ? ya : yb);
  return start <= Math.min(hi, ya < yb ? yb : ya) ? start : Infinity;
}

/**
 * Ponto sob a cruz e mira (F55; doc 02 §6). A cada quadro: `begin` lança o raio da câmera contra o
 * mundo da predição a partir do plano do olho (nada entre a câmera e o jogador conta); `cylinder`
 * acrescenta corpos (criaturas, outros jogadores); `finish` dá o ponto e a direção do olho até ele
 * (`aimYaw`, `aimPitch`), aproximada da câmera se passar de 24°. Sem alocação.
 */
export class CrosshairAim {
  /** Raio da câmera (unitário) e o trecho dele que conta. */
  ox = 0;
  oy = 0;
  oz = 0;
  dx = 0;
  dy = 0;
  dz = -1;
  t0 = 0;
  /** Distância do primeiro contato no raio (ou o alcance) e o ponto. */
  t: number = CROSSHAIR.range;
  /** Até onde o mundo (terreno, colisores) deixa o raio ir. */
  worldT: number = CROSSHAIR.range;
  hit = false;
  /** Quem o raio pegou primeiro entre os corpos (`cylinder`), ou "". */
  body = "";
  x = 0;
  y = 0;
  z = 0;
  aimYaw = 0;
  aimPitch = 0;
  private ex = 0;
  private ey = 0;
  private ez = 0;

  begin(cx: number, cy: number, cz: number, yaw: number, pitch: number, ex: number, ey: number, ez: number, world: CameraWorld): void {
    const cp = Math.cos(pitch);
    this.ox = cx;
    this.oy = cy;
    this.oz = cz;
    this.dx = -Math.sin(yaw) * cp;
    this.dy = Math.sin(pitch);
    this.dz = -Math.cos(yaw) * cp;
    this.ex = ex;
    this.ey = ey;
    this.ez = ez;
    // Começa no plano do olho: o que está entre a câmera e o jogador não é alvo.
    this.t0 = Math.max(0, (ex - cx) * this.dx + (ey - cy) * this.dy + (ez - cz) * this.dz);
    this.body = "";
    this.worldT = CROSSHAIR.range;
    for (let a = this.t0; a < CROSSHAIR.range; a += CROSSHAIR.chunk) {
      const b = Math.min(CROSSHAIR.range, a + CROSSHAIR.chunk);
      const f = sweepSphere(world, cx + this.dx * a, cy + this.dy * a, cz + this.dz * a, cx + this.dx * b, cy + this.dy * b, cz + this.dz * b, 0, CROSSHAIR.step);
      if (f < 1) {
        this.worldT = a + (b - a) * f;
        break;
      }
    }
    this.t = this.worldT;
    this.hit = this.worldT < CROSSHAIR.range;
  }

  /** Um corpo (cilindro vertical) no caminho: vale se vier antes do que o raio já pegou. */
  cylinder(id: string, x: number, z: number, radius: number, bottom: number, top: number): void {
    const t = rayCylinder(this.ox, this.oy, this.oz, this.dx, this.dy, this.dz, this.t0, this.t, x, z, radius, bottom, top);
    if (t < this.t) {
      this.t = t;
      this.hit = true;
      this.body = id;
    }
  }

  /** Ponto e mira, com a câmera em (yaw, pitch). */
  finish(yaw: number, pitch: number): void {
    this.x = this.ox + this.dx * this.t;
    this.y = this.oy + this.dy * this.t;
    this.z = this.oz + this.dz * this.t;
    this.reaim(yaw, pitch);
  }

  /**
   * Mira do olho ao ponto deste quadro, cabendo nos 24° da câmera em (yaw, pitch). A cena chama de novo
   * na hora de mandar cada passo, com o olhar de agora: o mouse pode ter girado depois do quadro, e a
   * mira fora dos 25° da câmera do comando seria recusada (achado na revisão da F55).
   */
  reaim(yaw: number, pitch: number): void {
    const vx = this.x - this.ex;
    const vy = this.y - this.ey;
    const vz = this.z - this.ez;
    const flat = Math.sqrt(vx * vx + vz * vz);
    if (flat * flat + vy * vy < CROSSHAIR.minDistance * CROSSHAIR.minDistance) {
      this.aimYaw = yaw;
      this.aimPitch = pitch;
      return;
    }
    let ay = Math.atan2(-vx, -vz);
    let ap = Math.atan2(vy, flat);
    // Aproxima da câmera na mesma proporção em yaw e pitch até caber nos 24° (o ângulo não é linear em
    // yaw/pitch: duas ou três passadas chegam).
    for (let i = 0; i < 4; i++) {
      const cos = aimCos(yaw, pitch, ay, ap);
      if (cos >= COS_MAX) break;
      const k = (CROSSHAIR.maxDeviation * 0.999) / Math.acos(Math.max(-1, Math.min(1, cos)));
      let dyaw = ay - yaw;
      dyaw -= Math.round(dyaw / (2 * Math.PI)) * 2 * Math.PI;
      ay = yaw + dyaw * k;
      ap = pitch + (ap - pitch) * k;
    }
    if (aimCos(yaw, pitch, ay, ap) < AIM.cosMaxDeviation) {
      ay = yaw;
      ap = pitch;
    }
    this.aimYaw = ay;
    this.aimPitch = ap;
  }
}
